import { useCallback, useEffect, useMemo, useState } from "react";
import "./DashboardPage.css";
import { AppShell } from "../app/AppShell";
import { useMe } from "../app/AuthGate";
import { Button } from "../components/Button";
import { Alert, Card, Loading } from "../components/Card";
import { Icon } from "../components/Icon";
import { PageHead } from "../components/Page";
import { Select } from "../components/Field";
import { UsageCard } from "../components/Usage";
import { IssueDetailModal } from "../components/IssueDetail";
import { AttentionList, DashKpis, DashProgress, NaruCards, ProjectCards, dashFacts, naruRows } from "../components/Dash";
import { minutesSince } from "../components/IssueCard";
import type { Issue, Milestone } from "../lib/tasks";
import { errText, tasksApi } from "../lib/tasks";
import type { Project } from "../lib/project";
import { projectApi } from "../lib/project";
import type { RunnerRow } from "../lib/auth";
import { authApi } from "../lib/auth";
import { hole, rich, tx } from "../i18n";

/* ══ 대시보드(t-in-port-dashboard) — 위에서 아래로 「지금 어떤가 → 무엇이 막혔나 → 프로젝트마다」 ══
   ① 접이식 판: [지표 | 타임라인 | 나루 N] — 타임라인은 b3x-in 의 사용이력(30분 칸 · dbix) 그대로다.
   ② 프로젝트 고르기 + 진행률 ③ 주의 필요 ④ 프로젝트 카드. 줄을 누르면 보드와 같은 상세 모달.
   ⑤ 맨 아래 API · 애드온 상태는 b3x-in 이 원래 가진 것이라 남겼다.
   갱신은 타이머 없이 — 열 때 · 「새로고침」 · 상세에서 고친 뒤. 「n분 전 갱신」이 낡은 정도를 말한다. */

type Health = { ok: boolean; service: string; version: string; addons: string[] };
const API = import.meta.env.VITE_B3XIN_API_URL ?? "/api";
type Tab = "kpi" | "timeline" | "naru";
const FOLD_KEY = "b3x-in.dash.fold";

function readFold(): boolean {
  try {
    return localStorage.getItem(FOLD_KEY) === "1";
  } catch {
    return false;
  }
}

export function DashboardPage() {
  const auth = useMe();
  const isAdmin = !auth || auth.me.role === "admin";
  const [list, setList] = useState<Issue[] | null>(null);
  const [ms, setMs] = useState<Milestone[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  // 러너 표는 관리자 문이다 — 못 받으면 null(「모름」)이고, 나루 카드는 열린 세션만으로 그린다.
  const [runners, setRunners] = useState<RunnerRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [at, setAt] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const [tab, setTab] = useState<Tab>("kpi");
  const [fold, setFold] = useState(readFold);
  const [project, setProject] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [health, setHealth] = useState<Health | null>(null);
  const [healthErr, setHealthErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [ts, m] = await Promise.all([tasksApi.list(), tasksApi.milestones()]);
      setList(ts);
      setMs(m);
      setErr(null);
      setAt(Date.now());
    } catch (e) {
      setErr(errText(e));
    }
    // 이름표 · 러너는 곁가지다 — 못 받아도 본 목록은 그린다.
    projectApi.list().then(setProjects, () => undefined);
    if (isAdmin) authApi.runners().then(setRunners, () => setRunners(null));
  }, [isAdmin]);

  useEffect(() => {
    void load();
    fetch(`${API}/health`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(setHealth)
      .catch((e: Error) => setHealthErr(e.message));
  }, [load]);
  // 「n분 전」을 다시 쓰는 시계 — 요청은 안 한다.
  useEffect(() => {
    const h = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(h);
  }, []);

  const scoped = useMemo(() => (list ?? []).filter((t) => !project || (t.projectId ?? "") === project), [list, project]);
  const facts = useMemo(() => dashFacts(scoped), [scoped]);
  const narus = useMemo(() => naruRows(runners, list ?? []), [runners, list]);
  const names = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p.name])), [projects]);
  const open = openId ? (list ?? []).find((t) => t.id === openId) ?? null : null;

  const toggleFold = () =>
    setFold((f) => {
      try {
        localStorage.setItem(FOLD_KEY, f ? "0" : "1");
      } catch {
        /* 저장 못 해도 이번 화면에서는 접힌다 */
      }
      return !f;
    });

  return (
    <AppShell route="/">
      <PageHead title={tx("nav.dashboard")} lead={tx("dash.lead")} />

      <section className="dashp__panel" aria-label={tx("dashx.panel")}>
        <header className="dashp__bar">
          <div className="b-tabs" role="tablist" aria-label={tx("dashx.panel")}>
            {(["kpi", "timeline", "naru"] as Tab[]).map((k) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={tab === k}
                className={`b-tab${tab === k ? " is-on" : ""}`}
                onClick={() => {
                  setTab(k);
                  if (fold) toggleFold();
                }}
              >
                {tx(`dashx.tab.${k}`)}
                {k === "naru" ? <span className="b-tab-count">{narus.length}</span> : null}
              </button>
            ))}
          </div>
          <span className="dashp__at">{at ? tx("dashx.updated", { t: minutesSince(new Date(at).toISOString(), now) }) : null}</span>
          <Button size="sm" variant="ghost" onClick={() => void load()}>
            <Icon name="refresh" /> {tx("common.action.refresh")}
          </Button>
          <Button size="sm" variant="ghost" icon onClick={toggleFold} aria-expanded={!fold} aria-label={tx(fold ? "dashx.unfold" : "dashx.fold")} title={tx(fold ? "dashx.unfold" : "dashx.fold")}>
            <Icon name={fold ? "chevronDown" : "chevronUp"} />
          </Button>
        </header>
        {!fold ? (
          <div className="dashp__panelbody">
            {tab === "kpi" ? <Card>{list ? <DashKpis k={facts.kpi} /> : <Loading text={tx("common.state.loading")} />}</Card> : null}
            {tab === "timeline" ? <UsageCard isAdmin={isAdmin} /> : null}
            {tab === "naru" ? (
              <Card>
                <NaruCards rows={narus} now={now} note={runners ? undefined : tx("dashx.naru.adminOnly")} />
              </Card>
            ) : null}
          </div>
        ) : null}
      </section>

      {err ? <Alert tone="error">{tx("dashx.loadFailed", { err })}</Alert> : null}
      {!list && !err ? <Loading text={tx("common.state.loading")} /> : null}

      {list ? (
        <>
          <div className="dashp__scope">
            <Select size="md" value={project} onChange={(e) => setProject(e.target.value)} aria-label={tx("dashx.scope")}>
              <option value="">{tx("dashx.allProjects")}</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
            <div className="dashp__meter">
              <DashProgress done={facts.done} total={facts.total} />
            </div>
          </div>

          <Card title={tx("dashx.attention.title", { n: facts.attention.length })}>
            <AttentionList rows={facts.attention} onOpen={setOpenId} now={now} />
          </Card>

          <h2 className="dashp__h">{tx("dashx.projects.title", { p: facts.projects.length, n: facts.kpi.open })}</h2>
          <ProjectCards projects={facts.projects} names={names} onOpen={setOpenId} />
        </>
      ) : null}

      <Card title={tx("dashx.system")}>
        {healthErr ? (
          <Alert tone="error">{rich(tx("dash.apiDown", { err: healthErr, ...hole("url") }), { url: <code>{API}/health</code> })}</Alert>
        ) : null}
        <dl className="kv">
          <dt>API</dt>
          <dd>{health ? tx("dash.apiOk", { version: health.version }) : healthErr ? tx("dash.apiFail", { err: healthErr }) : tx("dash.apiChecking")}</dd>
          <dt>{tx("dash.serverAddons")}</dt>
          <dd>{health ? (health.addons.length ? health.addons.join(", ") : tx("dash.coreOnly")) : "—"}</dd>
          <dt>{tx("dash.webAddons")}</dt>
          <dd>{__B3XIN_ADDONS__.length ? __B3XIN_ADDONS__.join(", ") : tx("dash.coreOnly")}</dd>
        </dl>
      </Card>

      {open && list ? (
        <IssueDetailModal
          t={open}
          all={list}
          onOpen={setOpenId}
          onClose={() => setOpenId(null)}
          epicTitle={open.parentId ? list.find((x) => x.id === open.parentId)?.title : undefined}
          milestoneLabel={open.milestoneId ? ms.find((m) => m.id === open.milestoneId)?.title : undefined}
          projectLabel={open.projectId ? names[open.projectId] : undefined}
          onChanged={() => void load()}
          onDeleted={() => {
            setOpenId(null);
            void load();
          }}
        />
      ) : null}
    </AppShell>
  );
}
